import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { fetchUserPreferences, upsertUserPreferences, setChangelogLastSeen } from "@/api/user-preferences";
import { useAuth } from "@/hooks/useAuth";
import { useOrgId } from "@/hooks/useOrganization";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";
import { isChangelogIdNewer } from "@/data/changelog";

export interface PersonalizationPreferences {
  pinnedReports: string[];
  pinnedSavedViews: string[];
  pinnedPages: string[];
  staffQuickActions: string[];
  dashboardLayouts?: Record<string, DashboardLayoutPreference>;
  welcomeCompleted?: boolean;
  /**
   * Primary role the user picked in the WelcomeFlow modal — e.g.
   * "Church Administrator", "Pastor/Minister". Used by personalization
   * surfaces (feature highlighting, default landing pages) when present.
   * Optional: legacy users who never went through onboarding have it
   * undefined and personalization falls back to defaults.
   */
  primaryRole?: string;
  /**
   * Id of the newest changelog entry (see src/data/changelog.ts) the user has
   * viewed. Compared against the newest entry's id to decide whether to show
   * the "what's new" auto-popup and unseen badge. Undefined for a user who
   * has never opened it.
   */
  lastSeenChangelogId?: string;
}

export interface DashboardLayoutPreference {
  hiddenWidgets: string[];
  order: string[];
  useFullWidgetSet?: boolean;
  role?: {
    hiddenWidgets: string[];
    order: string[];
  };
  full?: {
    hiddenWidgets: string[];
    order: string[];
  };
}

const defaultPreferences: PersonalizationPreferences = {
  pinnedReports: [],
  pinnedSavedViews: [],
  pinnedPages: [],
  staffQuickActions: [],
  dashboardLayouts: {},
  welcomeCompleted: false,
};

export const PERSONALIZATION_STORAGE_PREFIX = "personalization-preferences";
const blockedPreferenceScopes = new Set<string>();

function buildPreferenceScopeKey(userId: string | null | undefined, orgId: string | null | undefined) {
  if (!userId || !orgId) return null;
  return `${orgId}:${userId}`;
}

function isPreferenceScopeBlocked(scopeKey: string | null) {
  if (!scopeKey) return false;
  return blockedPreferenceScopes.has(scopeKey);
}

function markPreferenceScopeBlocked(scopeKey: string | null) {
  if (!scopeKey) return;
  blockedPreferenceScopes.add(scopeKey);
}

function getStorageKey(userId: string | null | undefined, orgId: string | null | undefined) {
  if (!userId || !orgId) return null;
  return `${PERSONALIZATION_STORAGE_PREFIX}:${orgId}:${userId}`;
}

function arePreferenceListsEqual(a: readonly string[], b: readonly string[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function areDashboardPreferenceSlicesEqual(
  a?: { hiddenWidgets: readonly string[]; order: readonly string[] },
  b?: { hiddenWidgets: readonly string[]; order: readonly string[] },
) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    arePreferenceListsEqual(a.hiddenWidgets, b.hiddenWidgets) &&
    arePreferenceListsEqual(a.order, b.order)
  );
}

function areDashboardLayoutPreferencesEqual(
  a?: DashboardLayoutPreference,
  b?: DashboardLayoutPreference,
) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.useFullWidgetSet === b.useFullWidgetSet &&
    areDashboardPreferenceSlicesEqual(a, b) &&
    areDashboardPreferenceSlicesEqual(a.role, b.role) &&
    areDashboardPreferenceSlicesEqual(a.full, b.full)
  );
}

function areDashboardLayoutsEqual(
  a?: Record<string, DashboardLayoutPreference>,
  b?: Record<string, DashboardLayoutPreference>,
) {
  const left = a ?? {};
  const right = b ?? {};
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (!arePreferenceListsEqual(leftKeys, rightKeys)) return false;
  return leftKeys.every((key) =>
    areDashboardLayoutPreferencesEqual(left[key], right[key]),
  );
}

function arePreferencesEqual(a: PersonalizationPreferences, b: PersonalizationPreferences) {
  return (
    arePreferenceListsEqual(a.pinnedReports, b.pinnedReports) &&
    arePreferenceListsEqual(a.pinnedSavedViews, b.pinnedSavedViews) &&
    arePreferenceListsEqual(a.pinnedPages, b.pinnedPages) &&
    arePreferenceListsEqual(a.staffQuickActions, b.staffQuickActions) &&
    areDashboardLayoutsEqual(a.dashboardLayouts, b.dashboardLayouts) &&
    a.welcomeCompleted === b.welcomeCompleted &&
    a.primaryRole === b.primaryRole &&
    a.lastSeenChangelogId === b.lastSeenChangelogId
  );
}

function readPreferences(storageKey: string | null): PersonalizationPreferences | null {
  if (!storageKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as PersonalizationPreferences;
  } catch (error) {
    console.debug("[personalization] unable to read preferences", error);
    return null;
  }
}

function writePreferences(storageKey: string | null, preferences: PersonalizationPreferences) {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preferences));
  } catch (error) {
    console.debug("[personalization] unable to write preferences", error);
  }
}

// ---------------------------------------------------------------------------
// Single source of truth.
//
// This hook used to keep its state in a per-render `useState`, which meant
// EVERY component that called it (AppSidebar, WelcomeFlow, Reports,
// ReportRunner, QuickActionsWidget) got an independent copy of `preferences`,
// its own server fetch, and its own server write. Because each write replaces
// the entire `preferences` JSONB document, the instances clobbered each other:
// when WelcomeFlow wrote `welcomeCompleted: true`, the still-mounted
// AppSidebar instance (holding the stale default `false`) would overwrite it
// on its next write. Net effect: `welcomeCompleted` and `primaryRole` never
// survived in the database, so the onboarding modal reappeared every login.
//
// The fix is a module-level store (mirroring useAuth's useSyncExternalStore
// pattern): one hydration, one write path, one shared state for the active
// (org, user) scope. Full-document writes are now safe because there is only
// one writer holding the complete, current document.
// ---------------------------------------------------------------------------

type StoreSnapshot = {
  scopeKey: string | null;
  preferences: PersonalizationPreferences;
  isReady: boolean;
};

let storeSnapshot: StoreSnapshot = {
  scopeKey: null,
  preferences: defaultPreferences,
  isReady: false,
};
const storeListeners = new Set<() => void>();

// The scope (org+user) we are currently hydrating/hydrated for, the token used
// to discard stale async hydrations when the scope changes mid-flight, whether
// the server already had a row (so seed defaults don't override server intent),
// and the seed defaults accumulated from every caller's `overrides`.
let activeScopeKey: string | null = null;
let hydrateToken = 0;
let hadServerRow = false;
let accumulatedSeeds: Partial<PersonalizationPreferences> = {};

function emitStore() {
  storeListeners.forEach((listener) => listener());
}

function setStoreSnapshot(next: StoreSnapshot) {
  if (
    storeSnapshot.scopeKey === next.scopeKey &&
    storeSnapshot.isReady === next.isReady &&
    arePreferencesEqual(storeSnapshot.preferences, next.preferences)
  ) {
    return;
  }
  storeSnapshot = next;
  emitStore();
}

function subscribeStore(listener: () => void) {
  storeListeners.add(listener);
  return () => {
    storeListeners.delete(listener);
  };
}

function getStoreSnapshot() {
  return storeSnapshot;
}

function persistPreferences(
  scopeKey: string,
  storageKey: string | null,
  orgId: string,
  userId: string,
  preferences: PersonalizationPreferences,
  options?: {
    // Codex P2, PR #6347 (dev -> main promotion): when set and `preferences`
    // already equals this snapshot, the network write below is skipped --
    // see its call site in hydrateScope for why. Deliberately does NOT touch
    // the localStorage mirror below, which must stay unconditional; see the
    // next comment for why that one is load-bearing on its own.
    skipNetworkIfEqualTo?: PersonalizationPreferences;
  },
) {
  // The localStorage mirror must run unconditionally, even once a scope is
  // blocked — isPreferenceScopeBlocked only exists to stop retrying a network
  // call already known to fail. It was never meant to also suppress the local
  // write: skipping it too meant every write after a block only updated
  // in-memory state for the current page session and was lost on reload,
  // which is what made a blocked scope's "seen" write (or any preference
  // write) look like it kept reverting on the next visit.
  writePreferences(storageKey, preferences);
  if (isPreferenceScopeBlocked(scopeKey)) return;
  if (options?.skipNetworkIfEqualTo && arePreferencesEqual(preferences, options.skipNetworkIfEqualTo)) {
    return;
  }
  void upsertUserPreferences(orgId, userId, preferences).catch((error) => {
    if (!isSupabaseUnavailableError(error)) {
      console.debug("[personalization] unable to persist preferences", error);
      return;
    }
    markPreferenceScopeBlocked(scopeKey);
  });
}

// Apply seed defaults only where the user has no meaningful value yet. A slice
// is "unseeded" when the key is absent, or it is an empty array on a brand-new
// user (no server row). Server-provided values always win.
function seedEmptyKeys(
  base: PersonalizationPreferences,
  seeds: Partial<PersonalizationPreferences>,
): PersonalizationPreferences {
  const next: PersonalizationPreferences = { ...base };
  let changed = false;
  for (const key of Object.keys(seeds) as (keyof PersonalizationPreferences)[]) {
    const seedValue = seeds[key];
    if (seedValue === undefined) continue;
    const currentValue = next[key];
    const isUnseeded =
      currentValue === undefined ||
      (Array.isArray(currentValue) && currentValue.length === 0 && !hadServerRow);
    if (isUnseeded) {
      (next as Record<string, unknown>)[key] = seedValue;
      changed = true;
    }
  }
  return changed ? next : base;
}

async function hydrateScope(
  scopeKey: string,
  storageKey: string | null,
  orgId: string,
  userId: string,
) {
  const token = ++hydrateToken;
  setStoreSnapshot({
    scopeKey,
    preferences: { ...defaultPreferences, ...accumulatedSeeds },
    isReady: false,
  });

  const stored = readPreferences(storageKey);

  if (isPreferenceScopeBlocked(scopeKey)) {
    if (token !== hydrateToken) return;
    const merged = stored
      ? { ...defaultPreferences, ...accumulatedSeeds, ...stored }
      : { ...defaultPreferences, ...accumulatedSeeds };
    setStoreSnapshot({ scopeKey, preferences: merged, isReady: true });
    return;
  }

  try {
    const serverData = await fetchUserPreferences(orgId, userId);
    if (token !== hydrateToken) return;

    let merged: PersonalizationPreferences;
    // Codex P2, PR #6347 (dev -> main promotion): only set in the branch
    // below where a server row actually exists to compare against -- see
    // this function's persistPreferences call for why.
    let serverNormalized: PersonalizationPreferences | null = null;
    if (serverData?.preferences) {
      hadServerRow = true;
      merged = {
        ...defaultPreferences,
        ...accumulatedSeeds,
        ...serverData.preferences,
        ...(stored ?? {}),
      };
      serverNormalized = { ...defaultPreferences, ...serverData.preferences };
      // lastSeenChangelogId is the one field where "local wins" (the spread
      // order above) is actively harmful rather than merely a design
      // tradeoff. It's meant to advance monotonically across every
      // device/session for a given user. If this device's local cache
      // predates a newer value the server already has (e.g. the user viewed
      // the changelog on another device first), letting local win here would
      // regress the merged result — and persistPreferences() just below
      // writes `merged` straight back to the server, permanently undoing the
      // other device's "seen" write. Reconcile just this field; every other
      // key keeps the general local-wins merge above unchanged. When only
      // one side has a value, the spread above already produced the right
      // result, so only the both-defined case needs overriding.
      //
      // isChangelogIdNewer (@/data/changelog), not a raw `>` — changelog ids
      // are `YYYY-MM-DD` or `YYYY-MM-DD-N` for the Nth same-day entry, and a
      // plain string comparison breaks once N reaches double digits (e.g.
      // "...-10" < "...-9" as strings, even though -10 shipped later); see
      // that function's own doc comment for the full reasoning. This is the
      // same comparison rule ChangelogModal.tsx uses for its unseen check
      // and mark-seen write guard — one rule, not several copies that could
      // silently disagree.
      const serverSeenId = serverData.preferences.lastSeenChangelogId;
      const localSeenId = stored?.lastSeenChangelogId;
      if (serverSeenId !== undefined && localSeenId !== undefined) {
        merged.lastSeenChangelogId = isChangelogIdNewer(localSeenId, serverSeenId)
          ? localSeenId
          : serverSeenId;
      }
    } else {
      hadServerRow = false;
      merged = stored
        ? { ...defaultPreferences, ...accumulatedSeeds, ...stored }
        : { ...defaultPreferences, ...accumulatedSeeds };
    }

    setStoreSnapshot({ scopeKey, preferences: merged, isReady: true });
    // Codex P2, PR #6347 (dev -> main promotion): the network write below
    // used to fire unconditionally on every hydration. Two devices that both
    // fetch the same old server snapshot race to write their own merge back
    // -- if device A advances a field (lastSeenChangelogId, most visibly:
    // the isChangelogIdNewer reconciliation above only protects THIS
    // hydration's own merge, not a second device's) and commits first, then
    // device B's still-in-flight hydration, having started from the older
    // snapshot, would write its stale merged copy over A's update, silently
    // undoing it. Comparing against the server's own row (normalized with
    // the same defaults `merged` already carries, so an older row missing a
    // newer field doesn't read as "different") and skipping the NETWORK
    // write specifically when nothing actually changed relative to it
    // removes the write that could regress a value another device already
    // advanced, without touching the case where local storage or a seed
    // genuinely adds something new, and without touching the localStorage
    // mirror inside persistPreferences, which stays unconditional.
    // serverNormalized is null when there was no server row at all (the
    // else branch above): nothing to diff against, so that write always
    // proceeds exactly as before, unaffected by this option.
    persistPreferences(scopeKey, storageKey, orgId, userId, merged, {
      skipNetworkIfEqualTo: serverNormalized ?? undefined,
    });
  } catch (error) {
    if (token !== hydrateToken) return;
    hadServerRow = false;
    const merged = stored
      ? { ...defaultPreferences, ...accumulatedSeeds, ...stored }
      : { ...defaultPreferences, ...accumulatedSeeds };
    setStoreSnapshot({ scopeKey, preferences: merged, isReady: true });
    if (isSupabaseUnavailableError(error)) {
      markPreferenceScopeBlocked(scopeKey);
    }
  }
}

function ensureScope(
  scopeKey: string | null,
  storageKey: string | null,
  orgId: string | null,
  userId: string | null,
  seeds: Partial<PersonalizationPreferences>,
) {
  // Logged out / no org — reset to defaults and stop. The hook short-circuits
  // its own `isReady`/`preferences` for the null scope, so the store snapshot
  // here just needs to forget the previous user's data.
  if (!scopeKey || !orgId || !userId) {
    if (activeScopeKey !== null) {
      activeScopeKey = null;
      accumulatedSeeds = {};
      hadServerRow = false;
      hydrateToken += 1;
    }
    setStoreSnapshot({ scopeKey: null, preferences: { ...defaultPreferences }, isReady: true });
    return;
  }

  if (activeScopeKey !== scopeKey) {
    activeScopeKey = scopeKey;
    accumulatedSeeds = { ...seeds };
    hadServerRow = false;
    void hydrateScope(scopeKey, storageKey, orgId, userId);
    return;
  }

  // Same scope already active — fold in any new seed defaults from this caller
  // and apply them to slices the user has not populated yet.
  accumulatedSeeds = { ...accumulatedSeeds, ...seeds };
  if (storeSnapshot.scopeKey === scopeKey && storeSnapshot.isReady) {
    const next = seedEmptyKeys(storeSnapshot.preferences, seeds);
    if (next !== storeSnapshot.preferences) {
      setStoreSnapshot({ scopeKey, preferences: next, isReady: true });
      persistPreferences(scopeKey, storageKey, orgId, userId, next);
    }
  }
}

function applyUpdate(
  scopeKey: string | null,
  storageKey: string | null,
  orgId: string | null,
  userId: string | null,
  next:
    | Partial<PersonalizationPreferences>
    | ((current: PersonalizationPreferences) => PersonalizationPreferences),
) {
  if (!scopeKey || !orgId || !userId) return;
  // Only mutate when the store is on this scope — guards against a write racing
  // ahead of (or behind) a scope switch.
  if (storeSnapshot.scopeKey !== scopeKey) return;

  // Do not accept writes against the speculative pre-hydration snapshot. A
  // later caller can mount while another component is still hydrating this
  // scope and fire a normalization setter from its mount effect. Persisting
  // that default snapshot before the server row arrives would replace the
  // user's real JSONB preferences (welcomeCompleted, primaryRole, etc.).
  if (!storeSnapshot.isReady) return;

  const current = storeSnapshot.preferences;
  const resolved = typeof next === "function" ? next(current) : { ...current, ...next };
  if (arePreferencesEqual(current, resolved)) return;

  setStoreSnapshot({ scopeKey, preferences: resolved, isReady: true });
  persistPreferences(scopeKey, storageKey, orgId, userId, resolved);
}

// Codex P2, PR #6347 (dev -> main promotion), round 2: setLastSeenChangelogId
// used to go through applyUpdate/persistPreferences like every other field --
// a full-document write gated only by comparing against THIS client's own
// (potentially stale) in-memory snapshot. A tab left open across a deploy,
// still running an older bundle, can pass that check against its own even
// staler stored value and regress a lastSeenChangelogId a fresher session
// already advanced. Routes this one field through the atomic, server-side
// set_personalization_changelog_seen RPC instead (src/api/user-preferences.ts),
// which can never regress the value, and adopts whatever the RPC reports as
// the field's true current value afterward -- not necessarily what this call
// sent -- so a stale client self-heals instead of compounding the staleness
// on its next write.
function applyChangelogSeenUpdate(
  scopeKey: string | null,
  storageKey: string | null,
  orgId: string | null,
  userId: string | null,
  changelogId: string,
) {
  if (!scopeKey || !orgId || !userId) return;
  if (storeSnapshot.scopeKey !== scopeKey) return;
  // Same reasoning as applyUpdate: never write against the speculative
  // pre-hydration snapshot.
  if (!storeSnapshot.isReady) return;

  const current = storeSnapshot.preferences;
  if (
    current.lastSeenChangelogId !== undefined &&
    !isChangelogIdNewer(changelogId, current.lastSeenChangelogId)
  ) {
    return;
  }

  // Optimistic local update so the UI (badge, auto-popup gate) reflects the
  // change immediately, mirrored to localStorage exactly like every other
  // write -- writePreferences here, not persistPreferences, since the
  // network side goes through the RPC below instead of upsertUserPreferences.
  const optimistic = { ...current, lastSeenChangelogId: changelogId };
  setStoreSnapshot({ scopeKey, preferences: optimistic, isReady: true });
  writePreferences(storageKey, optimistic);
  if (isPreferenceScopeBlocked(scopeKey)) return;

  void setChangelogLastSeen(orgId, userId, changelogId)
    .then((resolvedId) => {
      if (resolvedId === null || resolvedId === changelogId) return;
      // The RPC reports a value other than what this call sent -- a
      // concurrent write already advanced past it. Only ever move this
      // client's local state FORWARD to that true value, never backward:
      // a slow response landing after a newer local write (e.g. the user
      // re-opened and closed the modal again while this request was still
      // in flight) must not undo that newer write.
      if (storeSnapshot.scopeKey !== scopeKey) return;
      const latest = storeSnapshot.preferences;
      if (
        latest.lastSeenChangelogId === undefined ||
        isChangelogIdNewer(resolvedId, latest.lastSeenChangelogId)
      ) {
        const reconciled = { ...latest, lastSeenChangelogId: resolvedId };
        setStoreSnapshot({ scopeKey, preferences: reconciled, isReady: true });
        writePreferences(storageKey, reconciled);
      }
    })
    .catch((error) => {
      if (!isSupabaseUnavailableError(error)) {
        console.debug("[personalization] unable to persist changelog seen state", error);
        return;
      }
      markPreferenceScopeBlocked(scopeKey);
    });
}

/** Test-only: reset the module-level store between test cases. */
export function __resetPersonalizationStoreForTests() {
  storeSnapshot = { scopeKey: null, preferences: defaultPreferences, isReady: false };
  activeScopeKey = null;
  hydrateToken += 1;
  hadServerRow = false;
  accumulatedSeeds = {};
  blockedPreferenceScopes.clear();
  storeListeners.clear();
}

export function usePersonalizationPreferences(
  overrides?: Partial<PersonalizationPreferences>
) {
  const { user } = useAuth();
  const organizationId = useOrgId();
  const userId = user?.id ?? null;

  const scopeKey = useMemo(
    () => buildPreferenceScopeKey(userId, organizationId),
    [organizationId, userId]
  );
  const storageKey = useMemo(() => getStorageKey(userId, organizationId), [organizationId, userId]);
  const overridesKey = useMemo(() => JSON.stringify(overrides ?? {}), [overrides]);
  const mergedDefaults = useMemo(
    () => ({
      ...defaultPreferences,
      ...overrides,
    }),
    [overridesKey]
  );

  const snapshot = useSyncExternalStore(subscribeStore, getStoreSnapshot);

  useEffect(() => {
    ensureScope(scopeKey, storageKey, organizationId, userId, overrides ?? {});
    // `overrides` is captured via the stable `overridesKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, storageKey, organizationId, userId, overridesKey]);

  const scopeActive = scopeKey !== null && snapshot.scopeKey === scopeKey;
  const preferences = scopeActive ? snapshot.preferences : mergedDefaults;
  const isReady = scopeKey === null ? true : scopeActive ? snapshot.isReady : false;

  const updatePreferences = useCallback(
    (
      next:
        | Partial<PersonalizationPreferences>
        | ((current: PersonalizationPreferences) => PersonalizationPreferences)
    ) => {
      applyUpdate(scopeKey, storageKey, organizationId, userId, next);
    },
    [organizationId, scopeKey, storageKey, userId]
  );

  const setPinnedReports = useCallback(
    (next: string[]) => updatePreferences({ pinnedReports: next }),
    [updatePreferences]
  );
  const setPinnedSavedViews = useCallback(
    (next: string[]) => updatePreferences({ pinnedSavedViews: next }),
    [updatePreferences]
  );
  const setPinnedPages = useCallback(
    (next: string[]) => updatePreferences({ pinnedPages: next }),
    [updatePreferences]
  );
  const setStaffQuickActions = useCallback(
    (next: string[]) => updatePreferences({ staffQuickActions: next }),
    [updatePreferences]
  );
  const setDashboardLayoutPreference = useCallback(
    (layoutKey: string, layout: DashboardLayoutPreference) =>
      updatePreferences((current) => ({
        ...current,
        dashboardLayouts: {
          ...(current.dashboardLayouts ?? {}),
          [layoutKey]: layout,
        },
      })),
    [updatePreferences]
  );
  const setWelcomeCompleted = useCallback(
    (completed: boolean) => updatePreferences({ welcomeCompleted: completed }),
    [updatePreferences]
  );
  const setPrimaryRole = useCallback(
    (role: string | undefined) => updatePreferences({ primaryRole: role }),
    [updatePreferences]
  );
  const setLastSeenChangelogId = useCallback(
    (id: string) => applyChangelogSeenUpdate(scopeKey, storageKey, organizationId, userId, id),
    [scopeKey, storageKey, organizationId, userId]
  );

  return {
    preferences,
    setPinnedReports,
    setPinnedSavedViews,
    setPinnedPages,
    setStaffQuickActions,
    setDashboardLayoutPreference,
    setWelcomeCompleted,
    setPrimaryRole,
    setLastSeenChangelogId,
    isReady,
    /**
     * Identifies the (org, user) scope this snapshot belongs to — `${orgId}:
     * ${userId}`, or null with no org/user. Exposed so a caller that defers a
     * write across a hydration boundary (e.g. ChangelogModal's "mark seen on
     * close" effect) can detect an org switch in between and discard a write
     * that would otherwise land in the wrong scope.
     */
    scopeKey,
  };
}
